// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { APP_CONFIG } from '../config/app';

const AuthContext = createContext(null);
const API_BASE   = process.env.REACT_APP_API_URL || '';

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // [AWS multi-akun] Panggilan AI ikut membawa akun AWS yang sedang dipilih
  // (dropdown di halaman AWS, disimpan di 'awsinv.account'), supaya jawaban
  // AI tidak jatuh ke akun primary saat halaman Indomas (acct2) yang dibuka.
  if (/\/api\/ai\//.test(String(config.url || '')) && !config.headers['X-Aws-Account']) {
    try {
      const acct = localStorage.getItem('awsinv.account');
      if (acct) config.headers['X-Aws-Account'] = acct;
    } catch (_) {}
  }
  return config;
});

// FALLBACK mapping role → permission untuk role bawaan.
export const ROLE_DASHBOARDS = {
  SAP:   ['solman', 'sap_usage'],
  AWS:   ['aws_billing', 'aws_cost'],
  admin: ['solman', 'sap_usage', 'aws_billing', 'aws_cost',
          'logs', 'users', 'table_maintenance', 'role_management'],
};

export async function logActivity(action, detail = null) {
  try {
    await api.post('/api/logs/activity', { action, detail });
  } catch (_) { /* silent fail */ }
}

function effectivePermissions(user) {
  if (!user) return [];
  if (Array.isArray(user.permissions) && user.permissions.length) return user.permissions;
  return ROLE_DASHBOARDS[user.role] || [];
}

// ── Bersihkan kredensial dari URL tanpa reload halaman ────────────────────────
// Hapus ?token=, ?user=, ?pass= dari URL bar tapi tetap di halaman yang sama
function cleanUrlParams() {
  const url    = new URL(window.location.href);
  let changed  = false;
  ['token', 'user', 'pass'].forEach(p => {
    if (url.searchParams.has(p)) { url.searchParams.delete(p); changed = true; }
  });
  if (changed) window.history.replaceState({}, '', url.toString());
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(window.location.search);

      // ── METODE 1: ?token=<jwt> ─────────────────────────────────────────────
      // Langsung inject JWT ke localStorage, lalu verifikasi via /api/auth/me
      const urlToken = params.get('token');
      if (urlToken) {
        cleanUrlParams();
        try {
          localStorage.setItem('auth_token', urlToken);
          const res    = await api.get('/api/auth/me');
          const u      = res.data.user;
          const merged = { ...u, permissions: u.permissions || ROLE_DASHBOARDS[u.role] || [] };
          localStorage.setItem('auth_token', urlToken);
          localStorage.setItem('auth_user', JSON.stringify(merged));
          setUser(merged);
          setLoading(false);
          return; // selesai, skip cek lainnya
        } catch {
          // Token tidak valid — hapus dan lanjut ke flow normal
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
        }
      }

      // ── METODE 2: ?user=<username>&pass=<password> ────────────────────────
      // Auto-call login endpoint dengan kredensial dari URL
      const urlUser = params.get('user');
      const urlPass = params.get('pass');
      if (urlUser && urlPass) {
        cleanUrlParams(); // hapus kredensial dari URL SEGERA sebelum request
        try {
          const res = await api.post('/api/auth/login', {
            username: urlUser,
            password: urlPass,
          });
          const { token, user: userData } = res.data;
          const merged = {
            ...userData,
            permissions: userData.permissions || ROLE_DASHBOARDS[userData.role] || [],
          };
          localStorage.setItem('auth_token', token);
          localStorage.setItem('auth_user', JSON.stringify(merged));
          setUser(merged);
          setLoading(false);
          return; // selesai
        } catch {
          // Kredensial salah — lanjut ke halaman login normal
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          setLoading(false);
          return;
        }
      }

      // ── FLOW NORMAL: cek localStorage (session yang sudah ada) ────────────
      const token     = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('auth_user');
      if (token && savedUser) {
        try {
          setUser(JSON.parse(savedUser));
          api.get('/api/auth/me')
            .then(res => {
              const u      = res.data.user;
              const merged = { ...u, permissions: u.permissions || ROLE_DASHBOARDS[u.role] || [] };
              setUser(merged);
              localStorage.setItem('auth_user', JSON.stringify(merged));
            })
            .catch(() => { localStorage.clear(); setUser(null); });
        } catch { localStorage.clear(); }
      }
      setLoading(false);
    };

    init();
  }, []);

  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      const res = await api.post('/api/auth/login', { username, password });
      const { token, user: userData } = res.data;
      const merged = {
        ...userData,
        permissions: userData.permissions || ROLE_DASHBOARDS[userData.role] || [],
      };
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_user', JSON.stringify(merged));
      setUser(merged);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Terjadi kesalahan.';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    // Beri tahu modul cache per-user (mis. favorites.js) agar mem-flush
    // perubahan tertunda lalu reset. Pakai event, BUKAN import, supaya tidak
    // terjadi circular import ke AuthContext.
    //
    // URUTAN MENGIKAT: event dipancarkan SEBELUM auth_token dihapus. Interceptor
    // di baris 11 membaca token dari localStorage; kalau token sudah hilang,
    // PUT flush dari favorites.js kena 401 dan perubahannya tetap lenyap.
    try { window.dispatchEvent(new Event('auth-logout')); } catch (_) {}
    try { await api.post('/api/auth/logout'); } catch (_) {}
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setUser(null);
    setError(null);
  }, []);

  // ── Auto-logoff saat idle ─────────────────────────────────────────────────
  // Aktif hanya selama user login. Reset timer tiap ada aktivitas (di-throttle).
  // Saat timeout: tandai alasan ('idle') lalu logout — Login.js menampilkan notif.
  useEffect(() => {
    if (!user) return;

    let timerId;
    let lastActivity = 0;

    const doLogout = () => {
      try { sessionStorage.setItem('logout_reason', 'idle'); } catch (_) {}
      logout();
    };

    const resetTimer = () => {
      clearTimeout(timerId);
      timerId = setTimeout(doLogout, APP_CONFIG.IDLE_LOGOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivity < APP_CONFIG.IDLE_ACTIVITY_THROTTLE_MS) return;
      lastActivity = now;
      resetTimer();
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetTimer(); // mulai hitung sejak login

    return () => {
      clearTimeout(timerId);
      events.forEach(e => window.removeEventListener(e, onActivity));
    };
  }, [user, logout]);

  // ── Heartbeat: deteksi sesi yang sudah tidak sah ──────────────────────────
  // Ping ringan ke /api/auth/me tiap 60 dtk.
  //
  // DUA sebab, dan keduanya WAJIB ditangani — versi lama hanya menangkap yang
  // pertama, dan itu lubangnya:
  //   • 401 SESSION_REVOKED → sesi dicabut admin.
  //   • 403 → token KEDALUWARSA (`server.js:217`; masa berlaku 8 jam,
  //     `server.js:344`). Karena statusnya 403 dan bukan 401, cabang lama tidak
  //     pernah cocok. Akibatnya setelah 8 jam UI tetap TAMPAK login — `user`
  //     masih terisi, menu masih lengkap — tapi setiap permintaan ke backend
  //     gagal senyap sampai halaman di-reload. Gejalanya paling terlihat di AI
  //     Coding (spinner menggantung selamanya, 22 Agu 2026 ~19:57) karena hanya
  //     di situ ada pekerjaan yang menunggu lama, tapi penyakitnya seluruh
  //     dashboard.
  //
  // 403 dari endpoint LAIN tidak boleh dipakai untuk menyimpulkan hal ini —
  // di tempat lain 403 bisa berarti "tidak punya izin". Di sini aman karena
  // `/api/auth/me` tidak pernah memeriksa izin apa pun; satu-satunya sebab ia
  // memulangkan 403 adalah `jwt.verify` yang gagal.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(async () => {
      try { await api.get('/api/auth/me'); }
      catch (e) {
        const st = e?.response?.status;
        if (st === 401 && e?.response?.data?.error === 'SESSION_REVOKED') {
          try { sessionStorage.setItem('logout_reason', 'revoked'); } catch (_) {}
          logout();
        } else if (st === 403 || (st === 401 && !e?.response?.data?.error)) {
          try { sessionStorage.setItem('logout_reason', 'expired'); } catch (_) {}
          logout();
        }
        // Galat jaringan (tanpa `response`) sengaja DIABAIKAN: koneksi yang
        // putus sebentar bukan sesi yang berakhir, dan me-logout karenanya
        // akan membuang pekerjaan yang sedang berjalan.
      }
    }, 60000);
    return () => clearInterval(id);
  }, [user, logout]);

  const hasAccess = useCallback((dashboardKey) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return effectivePermissions(user).includes(dashboardKey);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, hasAccess, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth harus dalam AuthProvider');
  return ctx;
}
